import { FormControl } from "@mui/material"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Divider from "@mui/material/Divider"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { AddOutlined } from "@mui-symbols-material/w300"
import dayjs from "dayjs"
import { type FC } from "react"
import { useFieldArray, useFormContext } from "react-hook-form"
import { useShallow } from "zustand/shallow"

import { FieldGroup, FieldRow, Form, FormField } from "@/components/Forms"
import { AerosDatePicker } from "@/components/UiKit"
import { SELECT_STYLING_PROPS, TEXT_FIELD_STYLING_PROPS } from "@/constants/forms"
import type { Product, UnitOfMeasurement } from "@/graphql/codegen/graphql"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import { createUomsList } from "@/utils/conversion"

export const AddProductPriceBreakdown: FC<{
  product: Product
}> = ({ product }) => {
  const units = useUnitOfMeasurementsStore(useShallow((s) => s.units))
  const uomsDropdownList = createUomsList(units, product)

  const { control, setValue, getValues, trigger, watch } = useFormContext()
  const { fields, append } = useFieldArray({
    control,
    name: "pricingTiersToCreate",
  })

  const effectiveDate = watch("effectiveDate")
  const expirationDate = watch("expirationDate")

  const handlePricingUomChange = (selectedValue: string) => {
    const selectedUoM: UnitOfMeasurement | undefined = uomsDropdownList.find(({ id }) => id === selectedValue)
    if (!selectedUoM) {
      return
    }

    fields.forEach((_, index) => {
      setValue(`pricingTiersToCreate.${index}.unitOfMeasurement.id`, selectedUoM.id)
      setValue(`pricingTiersToCreate.${index}.unitOfMeasurement.name`, selectedUoM.name)
    })
  }

  const addPricingTierFields = () => {
    const selectedUoM = getValues("pricingTiersToCreate.0.unitOfMeasurement")
    append({ unitOfMeasurement: selectedUoM, numberOfUnits: "", pricePerUnit: "" })
  }

  return (
    <Form className='gap-6'>
      {fields.map((field, index) => (
        <Box key={field.id}>
          <Typography variant='body2' fontWeight={700} className='text-base text-gray-700'>
            Pricing Tier {index + 1} {index === 0 ? <span className='text-red-600'>&nbsp;*</span> : "(optional)"}
          </Typography>
          <Divider className='mb-5 mt-2' />
          <FieldGroup>
            <FieldRow className='items-baseline'>
              <FormField
                name={`pricingTiersToCreate.${index}.unitOfMeasurement.id`}
                label='Pricing UoM'
                required
                slotProps={{
                  label: {
                    className: "text-gray-800 text-[12px] font-normal",
                  },
                }}
                render={({ field, fieldState }) => (
                  <FormControl>
                    <Select<string>
                      {...field}
                      {...SELECT_STYLING_PROPS}
                      error={!!fieldState.error}
                      onChange={(e) => handlePricingUomChange(e.target.value)}
                    >
                      {uomsDropdownList.map(({ id, name }) => (
                        <MenuItem key={id} value={id}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
              <FormField
                name={`pricingTiersToCreate.${index}.numberOfUnits`}
                label='Product Quantity'
                required={index === 0}
                slotProps={{
                  label: {
                    className: "text-gray-800 text-[12px] font-normal",
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    {...TEXT_FIELD_STYLING_PROPS}
                    placeholder='Input quantity'
                    error={!!fieldState.error}
                  />
                )}
              />
              <FormField
                name={`pricingTiersToCreate.${index}.pricePerUnit`}
                label='Currency/Price per Unit'
                required={index === 0}
                slotProps={{
                  label: {
                    className: "text-gray-800 text-[12px] font-normal",
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    {...TEXT_FIELD_STYLING_PROPS}
                    placeholder='Input price per'
                    error={!!fieldState.error}
                  />
                )}
              />
            </FieldRow>
          </FieldGroup>
        </Box>
      ))}
      <Box className='flex w-full justify-end'>
        <Button startIcon={<AddOutlined />} onClick={addPricingTierFields} disabled={fields.length > 5}>
          Additional Tier
        </Button>
      </Box>
      <Divider className='mb-4 mt-2' />
      <FieldGroup>
        <FieldRow className='items-baseline'>
          <FormField
            name='effectiveDate'
            label='Effective Date'
            required
            slotProps={{
              label: {
                className: "text-gray-800 text-[12px] font-normal",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <AerosDatePicker
                {...field}
                value={field.value ? dayjs.utc(field.value).format("YYYY-MM-DD") : null}
                onChange={(value) => {
                  const formattedDate = value ? dayjs.utc(value).toISOString() : ""
                  field.onChange(formattedDate)
                  trigger("effectiveDate")
                  if (expirationDate) {
                    trigger("expirationDate")
                  }
                }}
                error={!!error}
                required
                data-testid='effective-date-picker'
              />
            )}
          />
          <FormField
            name='expirationDate'
            label='Valid Until Date'
            slotProps={{
              label: {
                className: "text-gray-800 text-[12px] font-normal",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <AerosDatePicker
                {...field}
                value={field.value ? dayjs.utc(field.value).format("YYYY-MM-DD") : null}
                onChange={(value) => {
                  const formattedDate = value ? dayjs.utc(value).toISOString() : ""
                  field.onChange(formattedDate)
                  trigger("expirationDate")
                  if (effectiveDate) {
                    trigger("effectiveDate")
                  }
                }}
                error={!!error}
                data-testid='expiration-date-picker'
              />
            )}
          />
        </FieldRow>
      </FieldGroup>
    </Form>
  )
}
